import { Fragment, ReactElement } from 'react';

import {
  PrimaryButton,
  TertiaryButton,
} from '@changegps/horizon-component-library';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { useBoolean } from 'usehooks-ts';

import { logger } from '@/utils';

import { MeetingTemplateData, useMutateMeetingTemplate } from '../../api';
import { meetingQuery } from '../../data/query-keys';
import { useMeetingTemplateFormContext } from '../../hooks/use-meeting-template-form-context';
import {
  resetTemplate,
  setTemplateAction,
  useGetTemplateAction,
  useGetTemplateId,
  useGetTemplateStatus,
} from '../../store';

import { DeleteMeetingTemplateDialog } from './DeleteMeetingTemplateDialog';

const MeetingHeaders = (): ReactElement => {
  const form = useMeetingTemplateFormContext();

  const templateAction = useGetTemplateAction();

  const templateId = useGetTemplateId();

  const templateStatus = useGetTemplateStatus();

  const queryClient = useQueryClient();

  const { value: shouldShowDeleteDialog, toggle: toggleDeleteDialog } =
    useBoolean();

  const { mutate: mutateMeetingTemplate, isPending } =
    useMutateMeetingTemplate();

  const handleReset = () => {
    form.reset();
    resetTemplate();
  };

  const statusHandler = (status: string) => {
    switch (status) {
      case 'Create':
        return 'User';
      case 'Update':
        return 'Modified';
      default:
        return 'Modified';
    }
  };

  const handleSubmit = form.handleSubmit((data) => {
    logger.log('data', data);

    const payload: MeetingTemplateData['param'] = {
      meeting_name: data.meeting_name,
      category: data.meeting_category,
      agenda: data.agenda,
      status: statusHandler(templateAction),
    };

    if (templateAction === 'Update') {
      payload.id = templateId;
    }

    if (templateStatus === 'Default') {
      payload.original_template_id = data.meeting_type;
    }

    mutateMeetingTemplate(payload, {
      onSuccess: () => {
        handleReset();
        void queryClient.invalidateQueries({
          queryKey: meetingQuery.getMeetingTypes,
        });
        toast.success('Meeting template saved successfully');
      },
      onError: (error) => {
        toast.error('Error saving meeting template');
        logger.error('error', error);
      },
    });
  });

  return (
    <Fragment>
      <Grid
        container
        alignItems="center"
        justifyContent="space-between"
        mb={3}
        spacing={2}
      >
        <Grid item xs={12} sm="auto">
          <PrimaryButton
            sx={{ textTransform: 'none' }}
            onClick={() => {
              handleReset();
              setTemplateAction('Create');
            }}
            disabled={templateAction === 'Create'}
          >
            Create New Meeting Template
          </PrimaryButton>
        </Grid>

        <Grid item xs={12} sm="auto">
          <Stack direction="row" gap={1} justifyContent="flex-end">
            {templateAction === 'Create' && (
              <TertiaryButton
                sx={{ textTransform: 'none' }}
                onClick={() => {
                  form.reset();
                  resetTemplate();
                }}
              >
                Cancel
              </TertiaryButton>
            )}

            {templateAction === 'Update' && (
              <TertiaryButton
                disabled={!templateId?.length}
                onClick={toggleDeleteDialog}
              >
                Delete
              </TertiaryButton>
            )}

            {templateAction === 'Update' && (
              <TertiaryButton
                onClick={handleReset}
                sx={{ textTransform: 'none' }}
              >
                Reset to default
              </TertiaryButton>
            )}

            <PrimaryButton
              disabled={isPending}
              sx={{ textTransform: 'none' }}
              onClick={handleSubmit}
            >
              Save template
            </PrimaryButton>
          </Stack>
        </Grid>
      </Grid>

      <DeleteMeetingTemplateDialog
        id={templateId || ''}
        open={shouldShowDeleteDialog}
        onClose={toggleDeleteDialog}
      />
    </Fragment>
  );
};

export default MeetingHeaders;
